import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";

const HazardModal = ({
  isOpen,
  closeEditor,
  children,
  title = "New Hazard Entry",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 800, height: 980 });
  const modalRef = useRef(null);
  const resizeDirection = useRef("");

  useEffect(() => {
    if (isOpen) {
      const maxWidth = window.innerWidth * 0.9;
      const maxHeight = window.innerHeight * 0.9;
      const initialWidth = Math.min(800, maxWidth);
      const initialHeight = Math.min(980, maxHeight);

      setSize({ width: initialWidth, height: initialHeight });

      setPosition({
        x: (window.innerWidth - initialWidth) / 2,
        y: (window.innerHeight - initialHeight) / 2,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleWindowResize = () => {
      setSize((prevSize) => {
        const maxWidth = window.innerWidth * 0.9;
        const maxHeight = window.innerHeight * 0.9;

        const newWidth = Math.min(prevSize.width, maxWidth);
        const newHeight = Math.min(prevSize.height, maxHeight);

        return { width: newWidth, height: newHeight };
      });

      setPosition((prevPosition) => {
        const newX = Math.max(
          0,
          Math.min(prevPosition.x, window.innerWidth - size.width)
        );
        const newY = Math.max(
          0,
          Math.min(prevPosition.y, window.innerHeight - size.height)
        );

        return { x: newX, y: newY };
      });
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [size]);

  const handleMouseDown = (e) => {
    if (e.target.closest(".modal-header")) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleResizeStart = (e, direction) => {
    e.stopPropagation();
    setIsResizing(true);
    resizeDirection.current = direction;
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      setPosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - size.width)),
        y: Math.max(0, Math.min(newY, window.innerHeight - size.height)),
      });
    } else if (isResizing) {
      const deltaX = e.movementX;
      const deltaY = e.movementY;

      setSize((prevSize) => {
        let newWidth = prevSize.width;
        let newHeight = prevSize.height;
        let newPosition = { ...position };

        if (resizeDirection.current.includes("right")) {
          newWidth = Math.min(
            prevSize.width + deltaX,
            window.innerWidth - position.x
          );
        } else if (resizeDirection.current.includes("left")) {
          newWidth = Math.min(prevSize.width - deltaX, window.innerWidth);
          newPosition.x = Math.min(
            position.x + deltaX,
            position.x + prevSize.width - 200
          );
        }

        if (resizeDirection.current.includes("bottom")) {
          newHeight = Math.min(
            prevSize.height + deltaY,
            window.innerHeight - position.y
          );
        } else if (resizeDirection.current.includes("top")) {
          newHeight = Math.min(prevSize.height - deltaY, window.innerHeight);
          newPosition.y = Math.min(
            position.y + deltaY,
            position.y + prevSize.height - 150
          );
        }

        setPosition(newPosition);

        return {
          width: Math.max(200, newWidth),
          height: Math.max(150, newHeight),
        };
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 9998 }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-lg overflow-hidden"
        style={{
          position: "absolute",
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${size.width}px`,
          height: `${size.height}px`,
          maxWidth: "90vw",
          maxHeight: "90vh",
          zIndex: 9999,
        }}
      >
        <div
          className="modal-header bg-gray-100 px-4 py-2 cursor-move flex justify-between items-center"
          onMouseDown={handleMouseDown}
        >
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded"
            onClick={closeEditor}
          >
            <X size={24} />
          </button>
        </div>
        <div
          className="p-4 overflow-auto"
          style={{
            height: "calc(100% - 40px)",
            position: "relative",
            zIndex: 10000,
          }}
        >
          {children}
        </div>
        <div
          className="resize-handle top"
          onMouseDown={(e) => handleResizeStart(e, "top")}
        />
        <div
          className="resize-handle right"
          onMouseDown={(e) => handleResizeStart(e, "right")}
        />
        <div
          className="resize-handle bottom"
          onMouseDown={(e) => handleResizeStart(e, "bottom")}
        />
        <div
          className="resize-handle left"
          onMouseDown={(e) => handleResizeStart(e, "left")}
        />
        <div
          className="resize-handle top-left"
          onMouseDown={(e) => handleResizeStart(e, "top-left")}
        />
        <div
          className="resize-handle top-right"
          onMouseDown={(e) => handleResizeStart(e, "top-right")}
        />
        <div
          className="resize-handle bottom-left"
          onMouseDown={(e) => handleResizeStart(e, "bottom-left")}
        />
        <div
          className="resize-handle bottom-right"
          onMouseDown={(e) => handleResizeStart(e, "bottom-right")}
        />
      </div>
    </div>,
    document.body
  );
};

export default HazardModal;
